import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Package, Truck, Briefcase, MapPin, FileText, CreditCard, CheckCircle,
  Zap, Clock, Calendar, MessageCircle, Camera, Heart, DollarSign, 
  ChevronDown, Star, ArrowRight, Phone
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { apiService } from '@/services/api';
import { generateWhatsAppLink, trackWhatsAppClick } from '@/utils/whatsappTemplates';
import { siteContent } from '@/data/siteContent';
import { TestimonialsGrid, TrustStatsBar } from '@/components/Testimonials';
import ShareEarnButton from '@/components/ShareEarnButton';
import { 
  CONTACT_INFO, SERVICE_TIERS, TRUST_BADGES, 
  SERVICES, HOW_IT_WORKS, FAQS 
} from '../data/mockData';

const { global, home, forms, errors } = siteContent;

const Home = () => {
  const [deliveryForm, setDeliveryForm] = useState({});
  const [miniMoveForm, setMiniMoveForm] = useState({});
  const [businessForm, setBusinessForm] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleWhatsAppClick = (templateName, variables = {}, context = {}) => {
    trackWhatsAppClick({ templateName, page: 'home', ...context });
    const link = generateWhatsAppLink(templateName, variables);
    window.open(link, '_blank');
  };

  const handleFormSubmit = async (e, formType) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = new FormData(e.target);
      const data = Object.fromEntries(formData.entries());

      let response;
      if (formType === 'delivery') {
        response = await apiService.submitDeliveryQuote(data);
        toast.success(forms.success.delivery.replace('{quoteId}', response.quote_id));
        // Open WhatsApp with form data
        setTimeout(() => {
          handleWhatsAppClick('delivery', { ...data, refId: response.quote_id }, { serviceType: 'delivery' });
        }, 1500);
      } else if (formType === 'minimove') {
        response = await apiService.submitMiniMoveQuote(data);
        toast.success(forms.success.miniMove.replace('{quoteId}', response.quote_id));
        setTimeout(() => {
          handleWhatsAppClick('miniMove', { ...data, refId: response.quote_id }, { serviceType: 'mini-move' });
        }, 1500);
      } else if (formType === 'business') {
        response = await apiService.submitBusinessApplication(data);
        toast.success(forms.success.business.replace('{applicationId}', response.application_id));
        setTimeout(() => {
          handleWhatsAppClick('business', { ...data, refId: response.application_id }, { serviceType: 'business' });
        }, 1500);
      }

      e.target.reset();
    } catch (error) {

      toast.error(errors.formError.generic);
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToQuote = (e) => {
    e.preventDefault();
    document.getElementById('quote')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen">
      {/* HERO SECTION */}
      <section className="relative bg-gradient-to-br from-[#001A47] via-[#002B6B] to-[#001A47] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#039fff] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#fb6402] rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 py-16 md:py-24 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              {home.hero.h1}
            </h1>
            <p className="text-xl md:text-2xl mb-4 text-[#039fff]">
              {home.hero.subHeadline}
            </p>
            <p className="text-sm md:text-base text-gray-300 mb-8 flex items-center justify-center flex-wrap gap-4">
              <span className="flex items-center"><CheckCircle className="h-4 w-4 mr-2 text-[#039fff]" /> Quote in minutes</span>
              <span className="flex items-center"><CheckCircle className="h-4 w-4 mr-2 text-[#039fff]" /> Real updates</span>
              <span className="flex items-center"><CheckCircle className="h-4 w-4 mr-2 text-[#039fff]" /> Proof-of-delivery</span>
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                size="lg" 
                className="bg-[#fb6402] hover:bg-[#e55a02] text-white px-8 py-6 text-lg w-full sm:w-auto"
                onClick={() => handleWhatsAppClick('generic', {}, { serviceType: 'general' })}
                data-testid="hero-whatsapp-btn"
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                {global.primaryCTA}
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white hover:text-[#001A47] px-8 py-6 text-lg w-full sm:w-auto"
                onClick={scrollToQuote}
                data-testid="hero-quote-btn"
              >
                {global.secondaryCTA}
              </Button>
            </div>
          </div>
        </div>

        {/* Decorative wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {TRUST_BADGES.map((badge, index) => {
              const Icon = {
                MessageCircle, Camera, Heart, DollarSign, MapPin, CreditCard
              }[badge.icon];
              
              return (
                <div key={index} className="flex flex-col items-center text-center">
                  <div className="bg-white rounded-full p-4 shadow-md mb-3">
                    <Icon className="h-6 w-6 text-[#039fff]" />
                  </div>
                  <p className="text-sm font-medium text-gray-700">{badge.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICE SELECTOR */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#001A47] mb-4">Our Services</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Whether you need a quick delivery or a full mini-move, we&apos;ve got you covered
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {SERVICES.map((service) => {
              const Icon = { Package, Truck, Briefcase }[service.icon];
              
              return (
                <Card key={service.id} className="hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-[#039fff]">
                  <CardContent className="p-8">
                    <div className="bg-[#039fff]/10 rounded-full p-4 w-16 h-16 flex items-center justify-center mb-6">
                      <Icon className="h-8 w-8 text-[#039fff]" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#001A47] mb-3">{service.title}</h3>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <ul className="space-y-2 mb-6">
                      {service.benefits.map((benefit, idx) => (
                        <li key={idx} className="flex items-start text-sm text-gray-700">
                          <CheckCircle className="h-4 w-4 text-[#fb6402] mr-2 flex-shrink-0 mt-0.5" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                    <Link to={service.link}>
                      <Button className="w-full bg-[#001A47] hover:bg-[#002B6B] text-white">
                        Learn More
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#001A47] mb-4">How It Works</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Getting your items moved is simple and transparent
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
            {HOW_IT_WORKS.map((step, index) => {
              const Icon = { MapPin, FileText, CreditCard, CheckCircle }[step.icon];
              
              return (
                <div key={step.step} className="relative">
                  {index < HOW_IT_WORKS.length - 1 && (
                    <div className="hidden lg:block absolute top-12 left-[60%] w-[80%] h-0.5 bg-[#039fff]/30"></div>
                  )}
                  <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow relative z-10">
                    <div className="bg-[#fb6402] text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl mb-4">
                      {step.step}
                    </div>
                    <Icon className="h-8 w-8 text-[#039fff] mb-4" />
                    <h3 className="text-xl font-bold text-[#001A47] mb-2">{step.title}</h3>
                    <p className="text-gray-600 text-sm">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICE TIERS */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#001A47] mb-4">Service Tiers</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the speed that fits your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {SERVICE_TIERS.map((tier) => {
              const Icon = { Zap, Clock, Calendar }[tier.icon];
              
              return (
                <div 
                  key={tier.id}
                  className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#039fff] hover:shadow-lg transition-all cursor-pointer"
                >
                  <Icon className="h-10 w-10 text-[#fb6402] mb-4" />
                  <h3 className="text-xl font-bold text-[#001A47] mb-2">{tier.name}</h3>
                  <p className="text-gray-700 mb-2">{tier.description}</p>
                  <p className="text-sm text-[#039fff] font-semibold">{tier.tagline}</p>
                </div>
              );
            })}
          </div>
          <p className="text-center text-sm text-gray-500 mt-6">
            Exact availability depends on area and capacity
          </p>
        </div>
      </section>

      {/* QUOTE BLOCK */}
      <section id="quote" className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#001A47] mb-4">Get Your Quote</h2>
              <p className="text-lg text-gray-600">
                Fill in your details and we&apos;ll get back to you in minutes
              </p>
            </div>

            <Card>
              <CardContent className="p-6 md:p-8">
                <Tabs defaultValue="delivery" className="w-full">
                  <TabsList className="grid w-full grid-cols-3 mb-8">
                    <TabsTrigger value="delivery">Delivery</TabsTrigger>
                    <TabsTrigger value="mini-move">Mini-Move</TabsTrigger>
                    <TabsTrigger value="business">Business</TabsTrigger>
                  </TabsList>

                  <TabsContent value="delivery">
                    <form onSubmit={(e) => handleFormSubmit(e, 'delivery')} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="pickup">Pickup Address *</Label>
                          <Input id="pickup" name="pickup" placeholder="e.g., Stellenbosch CBD" required />
                        </div>
                        <div>
                          <Label htmlFor="dropoff">Drop-off Address *</Label>
                          <Input id="dropoff" name="dropoff" placeholder="e.g., Somerset West" required />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="item">Item Description *</Label>
                        <Input id="item" name="item" placeholder="e.g., 3 boxes, documents" required />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="datetime">Preferred Date/Time *</Label>
                          <Input id="datetime" name="datetime" type="datetime-local" required />
                        </div>
                        <div>
                          <Label htmlFor="tier">Service Tier *</Label>
                          <select id="tier" name="tier" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" required>
                            <option value="now">Rocket Van NOW</option>
                            <option value="today">Rocket Van TODAY</option>
                            <option value="tomorrow">Rocket Van TOMORROW</option>
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="name">Your Name *</Label>
                          <Input id="name" name="name" placeholder="Full name" required />
                        </div>
                        <div>
                          <Label htmlFor="mobile">Mobile Number *</Label>
                          <Input id="mobile" name="mobile" type="tel" placeholder="+27" required />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="email">Email Address *</Label>
                        <Input id="email" name="email" type="email" placeholder="your@email.com" required />
                      </div>
                      <div>
                        <Label htmlFor="notes">Special Instructions</Label>
                        <Textarea id="notes" name="notes" placeholder="Stairs, access codes, etc." rows={3} />
                      </div>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <Button type="submit" disabled={isSubmitting} className="flex-1 bg-[#fb6402] hover:bg-[#e55a02]">
                          {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
                        </Button>
                        <Button 
                          type="button" 
                          variant="outline" 
                          className="flex-1"
                          onClick={() => handleWhatsAppClick('delivery', {}, { serviceType: 'delivery' })}
                        >
                          <MessageCircle className="mr-2 h-4 w-4" />
                          WhatsApp Instead
                        </Button>
                      </div>
                    </form>
                  </TabsContent>

                  <TabsContent value="mini-move">
                    <form onSubmit={(e) => handleFormSubmit(e, 'minimove')} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="pickup-mm">Pickup Location *</Label>
                          <Input id="pickup-mm" name="pickup" placeholder="e.g., Paarl" required />
                        </div>
                        <div>
                          <Label htmlFor="dropoff-mm">Drop-off Location *</Label>
                          <Input id="dropoff-mm" name="dropoff" placeholder="e.g., Cape Town CBD" required />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="items-mm">Item List *</Label>
                        <Textarea id="items-mm" name="items" placeholder="List furniture, appliances, boxes..." rows={3} required />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="floors">Floors/Lift Access *</Label>
                          <Input id="floors" name="floors" placeholder="e.g., 2nd floor, no lift" required />
                        </div>
                        <div>
                          <Label htmlFor="helpers">Helpers Needed *</Label>
                          <select id="helpers" name="helpers" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" required>
                            <option value="driver_only">Driver only</option>
                            <option value="plus_one">Driver + 1 helper</option>
                            <option value="plus_two">Driver + 2 helpers</option>
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="datetime-mm">Preferred Date/Time *</Label>
                          <Input id="datetime-mm" name="datetime" type="datetime-local" required />
                        </div>
                        <div>
                          <Label htmlFor="name-mm">Your Name *</Label>
                          <Input id="name-mm" name="name" placeholder="Full name" required />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="mobile-mm">Mobile Number *</Label>
                          <Input id="mobile-mm" name="mobile" type="tel" placeholder="+27" required />
                        </div>
                        <div>
                          <Label htmlFor="email-mm">Email Address *</Label>
                          <Input id="email-mm" name="email" type="email" placeholder="your@email.com" required />
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <Button type="submit" disabled={isSubmitting} className="flex-1 bg-[#fb6402] hover:bg-[#e55a02]">
                          {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
                        </Button>
                        <Button 
                          type="button" 
                          variant="outline" 
                          className="flex-1"
                          onClick={() => handleWhatsAppClick('miniMove', {}, { serviceType: 'mini-move' })}
                        >
                          <MessageCircle className="mr-2 h-4 w-4" />
                          WhatsApp Instead
                        </Button>
                      </div>
                    </form>
                  </TabsContent>

                  <TabsContent value="business">
                    <form onSubmit={(e) => handleFormSubmit(e, 'business')} className="space-y-4">
                      <div>
                        <Label htmlFor="business-name">Business Name</Label>
                        <Input id="business-name" placeholder="Your company name" />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="contact-person">Contact Person</Label>
                          <Input id="contact-person" placeholder="Full name" />
                        </div>
                        <div>
                          <Label htmlFor="email-bus">Email Address</Label>
                          <Input id="email-bus" type="email" placeholder="business@example.com" />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="mobile-bus">Mobile Number</Label>
                        <Input id="mobile-bus" type="tel" placeholder="+27" />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="volume">Monthly Volume Estimate</Label>
                          <select id="volume" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                            <option>1-10 deliveries/month</option>
                            <option>11-30 deliveries/month</option>
                            <option>31+ deliveries/month</option>
                          </select>
                        </div>
                        <div>
                          <Label htmlFor="area">Primary Pickup Area</Label>
                          <Input id="area" placeholder="e.g., Stellenbosch" />
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <Button type="submit" className="flex-1 bg-[#fb6402] hover:bg-[#e55a02]">
                          Apply for Business Account
                        </Button>
                        <Button 
                          type="button" 
                          variant="outline" 
                          className="flex-1"
                          onClick={() => handleWhatsAppClick('business', {}, { serviceType: 'business' })}
                        >
                          <MessageCircle className="mr-2 h-4 w-4" />
                          Continue on WhatsApp
                        </Button>
                      </div>
                    </form>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* WHY MZANIGO */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#001A47] mb-4">The Rocket Van Express Trust System</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We&apos;ve built our service around transparency, care, and communication
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: CheckCircle, title: 'Driver Verification', text: 'All drivers are verified with ID and background checks' },
              { icon: Camera, title: 'Pickup Confirmation', text: 'Photo confirmation when items are collected' },
              { icon: MessageCircle, title: 'Live WhatsApp Updates', text: 'Real-time updates throughout the delivery process' },
              { icon: Camera, title: 'Delivery Proof', text: 'Photo and signature proof with timestamp on completion' },
              { icon: Heart, title: 'Care-first Handling', text: 'Straps, blankets, and respectful service standard' },
              { icon: Phone, title: 'Clear Escalation', text: 'Direct support line for any issues or concerns' }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <Icon className="h-10 w-10 text-[#039fff] mb-4" />
                    <h3 className="text-lg font-bold text-[#001A47] mb-2">{item.title}</h3>
                    <p className="text-gray-600 text-sm">{item.text}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <TestimonialsGrid limit={6} columns={3} />
        </div>
      </section>

      {/* TRUST STATS */}
      <TrustStatsBar />

      {/* FAQ */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#001A47] mb-4">Frequently Asked Questions</h2>
              <p className="text-lg text-gray-600">
                Got questions? We&apos;ve got answers
              </p>
            </div>

            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left text-[#001A47] hover:text-[#039fff]">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* BUSINESS CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-[#001A47] to-[#002B6B] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Need Regular Deliveries?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Rocket Van Express Business offers monthly billing, priority dispatch, and consolidated invoicing for businesses
          </p>
          <Link to="/business">
            <Button size="lg" className="bg-[#fb6402] hover:bg-[#e55a02] text-white px-8 py-6">
              Apply for a Business Account
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Share & Earn Floating Button */}
      <ShareEarnButton />
    </div>
  );
};

export default Home;
